import type { CSSProperties } from 'react';
import { initials } from '../../lib/avatarInitials';

export type AvatarTone = 'client' | 'org' | 'direct' | 'default';

const TONE_STYLE: Record<AvatarTone, CSSProperties> = {
  client:  { background: 'var(--navy-700)', color: 'var(--teal-300)' },
  org:     { background: 'var(--teal-600)', color: '#fff' },
  direct:  { background: 'var(--navy-900)', color: 'var(--blue)' },
  default: { background: 'var(--navy-700)', color: 'var(--teal-300)' },
};

interface AvatarProps {
  name?: string | null;
  /** AppUser.avatarInitials / avatar_initials — a per-user override respected elsewhere in the app; takes priority over computed initials. */
  initialsOverride?: string | null;
  /** Renders '@' instead of initials (org broadcast thread). */
  isOrg?: boolean;
  size?: number;
  tone?: AvatarTone;
}

export function Avatar({ name, initialsOverride, isOrg, size = 32, tone = 'default' }: AvatarProps) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700,
      ...TONE_STYLE[tone],
    }}>
      {isOrg ? '@' : (initialsOverride || initials(name))}
    </div>
  );
}

interface PresenceDotProps {
  online: boolean;
  size?: number;
  /** Border color matching the surface the dot sits on, so it reads as a ring. */
  ringColor?: string;
}

export function PresenceDot({ online, size = 9, ringColor = 'var(--vs1)' }: PresenceDotProps) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: online ? 'var(--green)' : 'var(--slate-300)',
      border: `1.5px solid ${ringColor}`,
      flexShrink: 0,
    }} />
  );
}

interface AvatarStackProps {
  names: (string | null | undefined)[];
  max?: number;
  size?: number;
}

export function AvatarStack({ names, max = 4, size = 26 }: AvatarStackProps) {
  const shown = names.slice(0, max);
  const overflow = names.length - shown.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((n, i) => (
        <div key={i} style={{
          marginLeft: i === 0 ? 0 : -8,
          border: '2px solid var(--vs1)', borderRadius: '50%',
        }}>
          <Avatar name={n} size={size} />
        </div>
      ))}
      {overflow > 0 && (
        <span style={{
          marginLeft: 4, fontSize: '0.72rem', color: 'var(--shell-xs)', whiteSpace: 'nowrap',
        }}>
          +{overflow}
        </span>
      )}
    </div>
  );
}
